import PropTypes from 'prop-types'
import React from 'react'
import {
  findDOMNode
} from 'react-dom'
import {
  DragDropContext,
  DragSource,
  DropTarget
} from 'react-dnd'
import HTML5Backend from 'react-dnd-html5-backend'
import ClassNames from 'classnames'
import {
  ChildrenShape
} from 'shapes'
import HistoricalSensorChart from 'components/historical-sensor-chart'
import HistoricalEquipmentChart, { HistoricalInputChart } from 'components/historical-equipment-chart'
import CameraChart from 'components/camera-chart'
import withIsSmallScreen from 'enhancers/with-is-small-screen'
import noop from 'modules/noop'

import './sortable-analysis-charts.css'

const CHART_TYPE = 'chart'

function getDraggingOpacity (isDragging) {
  return isDragging ? 0.3 : 1
}

class AnalysisHistoricalChart extends React.PureComponent {
  static propTypes = {
    onRequestRemove: PropTypes.func.isRequired,
    isSensor: PropTypes.bool.isRequired,
    onChartClick: PropTypes.func.isRequired,

    isDragging: PropTypes.bool.isRequired,
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    unit: PropTypes.string,
    isSmallScreen: PropTypes.bool.isRequired,
    title: PropTypes.node.isRequired,
    children: ChildrenShape
  }

  render () {
    const {
      onRequestRemove,
      isSensor,

      isDragging,
      connectDragSource,
      connectDropTarget,
      unit,
      isSmallScreen,

      title,
      children
    } = this.props

    const classes = ClassNames({
      'analysis-historical-chart': true,
      'small-screen': isSmallScreen,
      'sensor': isSensor,
      'equipment': !isSensor
    })

    return connectDragSource(connectDropTarget(
      <div
        className={classes}
        style={{
          opacity: getDraggingOpacity(isDragging)
        }}>
        <div className="left-wrapper chart-details">
          <p className="chart-title">{title} {unit && unit.trim() ? <small>({unit})</small> : null}</p>
          <div className="chart-buttons-wrapper">
            {/* isSensor ? (
              <button className="chart-button green-button">Show Related</button>
            ) : null */}
            <div
              className="remove-chart-button"
              onClick={onRequestRemove}
            >remove?</div>
          </div>
        </div>

        <div className="right-wrapper" onClick={this.props.onChartClick}>
          {children}
        </div>
      </div>
    ))
  }
}

function dragCollect (connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
  }
}

function dropCollect (connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget()
  }
}

// Based off sortable example
// https://github.com/gaearon/react-dnd/blob/master/examples/04%20Sortable/Simple/Card.js
function handleHover (props, monitor, component) {
  const dragIndex = monitor.getItem().index
  const hoverIndex = props.index

  // Don't replace items with themselves
  if (dragIndex === hoverIndex) {
    return
  }

  // Determine rectangle on screen
  const hoverBoundingRect = findDOMNode(component).getBoundingClientRect()

  // Get vertical middle
  const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

  // Determine mouse position
  const clientOffset = monitor.getClientOffset()

  // Get pixels to the top
  const hoverClientY = clientOffset.y - hoverBoundingRect.top

  // Only perform the move when the mouse has crossed half of the items height
  // When dragging downwards, only move when the cursor is below 50%
  // When dragging upwards, only move when the cursor is above 50%

  // Dragging downwards
  if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
    return
  }

  // Dragging upwards
  if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
    return
  }

  props.onRequestReorder(dragIndex, hoverIndex)

  // Note: we're mutating the monitor item here!
  // Generally it's better to avoid mutations,
  // but it's good here for the sake of performance
  // to avoid expensive index searches.
  monitor.getItem().index = hoverIndex
}

function makeDraggable (component) {
  return DragSource(CHART_TYPE, {
    beginDrag: function (props) {
      return {
        index: props.index
      }
    }
  }, dragCollect)(DropTarget(CHART_TYPE, {
    hover: handleHover
  }, dropCollect)(component))
}

const DraggableAnalysisHistoricalChart = makeDraggable(withIsSmallScreen(AnalysisHistoricalChart))

class AnalysisCharts extends React.PureComponent {
  static propTypes = {
    registerToDataSource: PropTypes.object.isRequired,
    selectedRegisters: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChartClick: PropTypes.func,
    onRequestReorder: PropTypes.func.isRequired,
    onRequestRemoveIndex: PropTypes.func.isRequired,
    renderName: PropTypes.func.isRequired,
    xStart: PropTypes.string.isRequired,
    xEnd: PropTypes.string.isRequired,
    historicalDataByRegister: PropTypes.object.isRequired,
    connectivityData: PropTypes.array,
    highlightX: PropTypes.func
  }

  static defaultProps = {
    onChartClick: noop
  }

  state = {
    highlighted: null
  }

  handleReorder = (dragIndex, hoverIndex) => {
    const { selectedRegisters, onRequestReorder } = this.props
    const clonedRegisters = selectedRegisters.slice(0)

    const swap = clonedRegisters[dragIndex]
    clonedRegisters[dragIndex] = clonedRegisters[hoverIndex]
    clonedRegisters[hoverIndex] = swap

    onRequestReorder(clonedRegisters)
  }

  highlightX = (highlighted) => {
    this.setState({highlighted})
  }

  handleChartClick = () => {
    const {
      highlighted
    } = this.state

    if (highlighted) {
      this.props.onChartClick({
        timestamp: highlighted.x
      })
    }
  }

  renderChart = (register, index) => {
    const {
      registerToDataSource,
      xStart,
      xEnd,
      historicalDataByRegister,
      connectivityData
    } = this.props

    const { highlighted } = this.state
    const historicalData = historicalDataByRegister[register] || {}

    if (isSensorRegister(register)) {
      const sensor = registerToDataSource[register]

      if (!sensor) {
        return null
      }

      return (
        <HistoricalSensorChart
          sensor={sensor}
          historicalData={historicalData}
          connectivityData={connectivityData}
          xStart={xStart}
          xEnd={xEnd}
          highlighted={highlighted}
          highlightX={this.highlightX} />
      )
    } else if (isEquipmentRegister(register)) {
      const equipment = registerToDataSource[register]

      if (!equipment) {
        return null
      }

      return (
        <HistoricalEquipmentChart
          equipment={equipment}
          xStart={xStart}
          xEnd={xEnd}
          historicalData={historicalData}
          connectivityData={connectivityData}
          highlighted={highlighted}
          highlightX={this.highlightX} />
      )
    } else if (isInputRegister(register)) {
      const input = registerToDataSource[register]

      if (!input) {
        return null
      }

      return (
        <HistoricalInputChart
          equipment={input}
          xStart={xStart}
          xEnd={xEnd}
          historicalData={historicalData}
          connectivityData={connectivityData}
          highlighted={highlighted}
          highlightX={this.highlightX} />
      )
    } else {
      const camera = registerToDataSource[register]
      if (!camera) {
        return null
      }

      return (
        <CameraChart
          xStart={xStart}
          xEnd={xEnd}
          historicalCameraData={historicalData} />
      )
    }
  }

  renderDraggableCharts = () => {
    const {
      selectedRegisters,
      registerToDataSource,
      renderName
    } = this.props

    return selectedRegisters.map((register, index) => {
      const dataSource = registerToDataSource[register]

      if (!dataSource) return null

      return (
        <DraggableAnalysisHistoricalChart
          key={register}
          title={renderName(dataSource)}
          index={index}

          unit={dataSource.unit}

          onRequestReorder={this.handleReorder}
          onRequestRemove={() => {
            this.props.onRequestRemoveIndex(index)
          }}
          isSensor={isSensorRegister(register)}
          onChartClick={this.handleChartClick}
        >
          {this.renderChart(register, index)}
        </DraggableAnalysisHistoricalChart>
      )
    })
  }

  render () {
    return (
      <div className="analysis-charts-wrapper">
        {this.renderDraggableCharts()}
      </div>
    )
  }
}

export function isSensorRegister (register) {
  return register >= 2013
}

export function isEquipmentRegister (register) {
  return !isSensorRegister(register) && !isInputRegister(register) && register.toString().length < 5
}

export function isInputRegister (register) {
  return register.startsWith('i') && register.toString().length < 3
}

export function isCameraRegister (register) {
  return typeof register === 'string' && register.toString().length > 9
}

const SortableAnalysisCharts = DragDropContext(HTML5Backend)(AnalysisCharts)
export default SortableAnalysisCharts



// WEBPACK FOOTER //
// /Users/alextaxiera/source/farmhand-server/client/dashboard/src/js/screens/web/farm-data-analysis/sortable-analysis-charts.js